/**
 * ProposalShareDialog - Compartilhamento da proposta (WhatsApp Web / Chat interno)
 *
 * Permite montar a mensagem que será enviada ao cliente, com:
 *   - texto personalizado com variáveis da proposta/negócio/contato;
 *   - opção de incluir o link público da proposta;
 *   - opção de enviar o ARQUIVO PDF da proposta (documento real) pelo Chat
 *     interno, reaproveitando a mesma rota de anexos já usada na tela de chat
 *     (`sendDocumentToConversation`);
 *   - reaproveitamento das Ações Rápidas (macros) já existentes: é possível
 *     escolher uma macro de texto como base e salvar o texto atual como nova
 *     ação rápida (RPC create_routing_rule).
 *
 * Destinos:
 *   - WhatsApp: abre wa.me (WhatsApp Web no desktop / app no celular). O
 *     wa.me aceita apenas texto — não é possível anexar arquivo por esse
 *     caminho, então a opção de PDF fica indisponível nesse destino;
 *   - Chat: escolhe a inbox (StartChatInboxPicker), cria/obtém a conversa e,
 *     quando marcado, envia PRIMEIRO a mensagem de texto e DEPOIS o PDF como
 *     documento (duas mensagens). Sem PDF, mantém o comportamento de rascunho
 *     no campo de digitação.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v2.0.0 - Padronização visual: adota exatamente o mesmo
 *            layout do modal "Agendar mensagem" do chat (ScheduleMessageDialog):
 *            diálogo largo split-pane, cabeçalho premium, seções em
 *            FormSectionCard, barra de formatação WhatsApp + EmojiPicker,
 *            chips de variáveis e preview do balão em tempo real.
 *            Nenhuma regra de negócio foi alterada.
 *   RELEASE: 2026-08-12 v1.5.0 - A proposta só passa para "enviada" APÓS o envio
 *            real ao cliente (chat interno ou WhatsApp). A geração do link
 *            público deixou de marcar o status (send_deal_proposal v2.0.0).
 *   RELEASE: 2026-08-12 v1.4.0 - Botão "+" (criar ação rápida) e botão "olho"
 *            (visualizar todas as ações/sequências da ação rápida selecionada),
 *            reaproveitando CreateManualRuleDialog e RulePreviewDialog do chat.
 *   RELEASE: 2026-08-12 v1.3.0 - Texto passa a ser enviado como mensagem própria
 *            antes do PDF (legenda de documento não é confiável nos providers e
 *            não era exibida no chat interno).
 *   RELEASE: 2026-08-12 v1.2.0 - Remove SELECT direto em `conversations`: o
 *            contexto de envio (inbox, destino, provider, canal) passa a vir da
 *            RPC segura `get_conversation_send_context`, com suporte a grupos.
 *   RELEASE: 2026-08-12 v1.1.0 - PDF passa a ser enviado como arquivo real pelo
 *            Chat (documento), em vez de link temporário no texto.
 *   RELEASE: 2026-08-12 v1.0.0 - Versão inicial.

 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bold,
  Eye,
  FileText,
  Italic,
  Loader2,
  MessageCircle,
  MessageSquare,
  Paperclip,
  Plus,
  Save,
  Sparkles,
  Strikethrough,
  Variable,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InlineNotice } from '@/components/ui/inline-notice';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { StartChatInboxPicker } from '@/components/crm/deals/StartChatInboxPicker';
import { setChatDraft } from '@/lib/chatDraft';
import { sendDocumentToConversation } from '@/lib/chat/sendDocument';
import { sendTextToConversation } from '@/lib/chat/sendText';
import { getConversationSendContext } from '@/lib/chat/getConversationSendContext';
// v1.5.0: marca a proposta como "enviada" somente após envio real ao cliente
import { useProposalShare } from '@/hooks/useDealProposals';
// v1.4.0: reaproveita os diálogos de Ações Rápidas já usados na tela de chat
import { RulePreviewDialog } from '@/components/tenant/routing/RulePreviewDialog';
import { CreateManualRuleDialog } from '@/components/tenant/routing/CreateManualRuleDialog';
// v2.0.0: mesmos componentes de apresentação do ScheduleMessageDialog (chat)
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { EmojiPicker } from '@/components/tenant/chat/EmojiPicker';
import { ReminderMessagePreview } from '@/components/reminders/preview/ReminderMessagePreview';



const logger = createLogger('ProposalShareDialog');

/** Contexto mínimo da proposta usado para resolver as variáveis */
export interface ProposalShareContext {
  proposalId: string;
  proposalNumber: string;
  total: number | null;
  currency: string;
  expiresAt: string | null;
  dealTitle?: string | null;
  contactId?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  /** Link público já gerado (quando existir) */
  publicUrl?: string | null;
  /** Caminho do PDF no storage (quando já gerado) */
  pdfStoragePath?: string | null;
}

interface ProposalShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: ProposalShareContext;
  /** Devolve o link temporário do PDF (signed URL) */
  getPdfSignedUrl: (path: string | null) => Promise<string | null>;
  /** Conversa pronta para abrir no chat interno (mensagem já em rascunho) */
  onOpenConversation: (conversationId: string) => void;
}

/** Variáveis disponíveis para o texto personalizado */
const VARIABLES: { token: string; label: string }[] = [
  { token: '{{contact_first_name}}', label: 'Primeiro nome' },
  { token: '{{contact_name}}', label: 'Nome completo' },
  { token: '{{proposta_numero}}', label: 'Nº da proposta' },
  { token: '{{proposta_total}}', label: 'Valor total' },
  { token: '{{proposta_validade}}', label: 'Validade' },
  { token: '{{negocio_titulo}}', label: 'Negócio' },
];

/** Formata moeda no padrão brasileiro */
function formatCurrency(value: number | null | undefined, currency = 'BRL'): string {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** Substitui as variáveis do texto pelos dados reais da proposta */
function resolveVariables(text: string, ctx: ProposalShareContext): string {
  const fullName = (ctx.contactName || '').trim().replace(/\s+/g, ' ');
  const firstName = fullName ? fullName.split(' ')[0] : 'Cliente';
  return text
    .replace(/\{\{contact_first_name\}\}/g, firstName)
    .replace(/\{\{contact_name\}\}/g, fullName || 'Cliente')
    .replace(/\{\{proposta_numero\}\}/g, ctx.proposalNumber || '')
    .replace(/\{\{proposta_total\}\}/g, formatCurrency(ctx.total, ctx.currency))
    .replace(
      /\{\{proposta_validade\}\}/g,
      ctx.expiresAt ? new Date(ctx.expiresAt).toLocaleDateString('pt-BR') : 'sem prazo definido',
    )
    .replace(/\{\{negocio_titulo\}\}/g, ctx.dealTitle || '');
}

/** Somente dígitos do telefone (formato aceito pelo wa.me) */
function onlyDigits(value: string | null | undefined): string {
  return (value || '').replace(/\D/g, '');
}

/**
 * v2.0.0 — Envolve a seleção atual do textarea com um marcador de formatação
 * WhatsApp (`*` negrito, `_` itálico, `~` tachado). Mesma implementação usada
 * pelo ScheduleMessageDialog do chat, para manter o comportamento idêntico.
 */
function applyFormatting(
  textarea: HTMLTextAreaElement | null,
  marker: string,
  setValue: (val: string) => void,
) {
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const current = textarea.value;
  const selected = current.substring(start, end);
  const wrapped = selected ? `${marker}${selected}${marker}` : `${marker}${marker}`;
  const next = current.substring(0, start) + wrapped + current.substring(end);
  setValue(next);
  requestAnimationFrame(() => {
    textarea.focus();
    const cursor = selected ? start + wrapped.length : start + marker.length;
    textarea.setSelectionRange(cursor, cursor);
  });
}


export function ProposalShareDialog({
  open,
  onOpenChange,
  context,
  getPdfSignedUrl,
  onOpenConversation,
}: ProposalShareDialogProps) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  // v1.5.0: usado apenas para marcar "enviada" após entrega real ao cliente
  const { sendProposal } = useProposalShare();

  /**
   * Marca a proposta como enviada (best-effort).
   * Nunca interrompe o fluxo de envio: falha aqui é apenas registrada em log.
   */
  const markProposalAsSent = async () => {
    try {
      await sendProposal.mutateAsync({ proposalId: context.proposalId, markAsSent: true });
    } catch (err: any) {
      logger.warn('Não foi possível marcar a proposta como enviada', { error: err?.message });
    }
  };

  // Texto padrão sugerido (usuário pode alterar livremente)
  const defaultText = useMemo(
    () =>
      `Olá {{contact_first_name}}, tudo bem?\n\nSegue a proposta {{proposta_numero}} no valor de {{proposta_total}}, válida até {{proposta_validade}}.\n\nQualquer dúvida, estou à disposição!`,
    [],
  );

  const [text, setText] = useState<string>(defaultText);
  const [includeLink, setIncludeLink] = useState(true);
  const [includePdf, setIncludePdf] = useState(false);
  const [preparing, setPreparing] = useState<'whatsapp' | 'chat' | null>(null);
  const [inboxPickerOpen, setInboxPickerOpen] = useState(false);
  // Criação de nova ação rápida a partir do texto atual
  const [savingMacro, setSavingMacro] = useState(false);
  const [macroName, setMacroName] = useState('');
  const [showMacroName, setShowMacroName] = useState(false);
  // v1.4.0: ação rápida selecionada + diálogos de visualização e criação
  const [selectedMacroId, setSelectedMacroId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [createMacroOpen, setCreateMacroOpen] = useState(false);
  // v2.0.0: referência do textarea (inserção de variáveis/emoji no cursor)
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /** Insere um token `{{...}}` na posição do cursor (padrão do chat). */
  const insertAtCursor = useCallback((token: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setText((prev) => `${prev}${token}`);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    setText((prev) => prev.substring(0, start) + token + prev.substring(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }, []);



  // Ações rápidas de texto (macros) disponíveis para o usuário
  const { data: macros } = useQuery({
    queryKey: ['quick-action-rules', currentTenant?.id, 'proposal-share'],
    enabled: open && !!currentTenant?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_manual_routing_rules', {
        p_inbox_id: null as any,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      return ((data as any[]) || []).filter((r) => r.action_type === 'send_text');
    },
  });

  // Ação rápida atualmente selecionada (usada no diálogo de visualização)
  const selectedMacro = useMemo(
    () => (macros || []).find((m: any) => m.id === selectedMacroId) ?? null,
    [macros, selectedMacroId],
  );

  /**
   * Monta a mensagem final (texto resolvido + link público opcional).
   * O PDF NÃO entra como link: ele é enviado como arquivo no fluxo do chat.
   */
  const buildMessage = (): string => {
    const parts: string[] = [resolveVariables(text, context).trim()];

    if (includeLink) {
      if (context.publicUrl) {
        parts.push(`Proposta online: ${context.publicUrl}`);
      } else {
        toast.warning('Gere o link público antes de incluí-lo na mensagem.');
      }
    }

    return parts.filter(Boolean).join('\n\n');
  };

  /** Compartilha via WhatsApp (WhatsApp Web no desktop, app no celular) */
  const handleWhatsApp = async () => {
    const phone = onlyDigits(context.contactPhone);
    if (!phone) {
      toast.error('Contato sem telefone cadastrado.');
      return;
    }
    setPreparing('whatsapp');
    try {
      // wa.me só aceita texto — avisa que o arquivo não segue por esse caminho
      if (includePdf) {
        toast.info('O arquivo PDF só pode ser anexado pelo Chat interno.');
      }
      const message = buildMessage();
      window.open(
        `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
        '_blank',
        'noopener,noreferrer',
      );
      // v1.5.0: compartilhamento explícito ao cliente marca a proposta enviada
      await markProposalAsSent();
      onOpenChange(false);
    } finally {
      setPreparing(null);
    }
  };

  /**
   * Conversa criada/obtida.
   *  - Com "enviar PDF": envia PRIMEIRO a mensagem de texto e DEPOIS o arquivo
   *    como documento real (mesma rota de anexos do chat). Duas mensagens
   *    separadas, pois a legenda de documento não é exibida de forma confiável.
   *  - Sem PDF: mantém o comportamento de rascunho no campo de digitação.
   */
  const handleConversationReady = async (conversationId: string) => {
    setPreparing('chat');
    try {
      const message = buildMessage();

      if (!includePdf) {
        setChatDraft(conversationId, message);
        onOpenConversation(conversationId);
        onOpenChange(false);
        return;
      }

      if (!user?.id) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      // Contexto de envio via RPC segura (sem SELECT direto em conversations):
      // resolve inbox, destino (telefone 1:1 ou external_id do grupo), provider
      // e canal, validando tenant + acesso do usuário à conversa.
      const sendContext = await getConversationSendContext(
        conversationId,
        currentTenant?.id ?? null,
      );

      const destination = sendContext.destination || onlyDigits(context.contactPhone);
      if (!destination) {
        toast.error(
          sendContext.isGroup
            ? 'Grupo sem identificador externo para envio.'
            : 'Contato sem telefone cadastrado.',
        );
        return;
      }

      const conversationPayload = {
        id: conversationId,
        inbox_id: sendContext.inboxId,
        phone: destination,
        contactId: sendContext.contactId,
        providerSlug: sendContext.providerSlug,
      };

      // PASSO 1: texto como mensagem própria (antes do arquivo)
      if (message.trim()) {
        try {
          const textResult = await sendTextToConversation({
            conversation: conversationPayload,
            userId: user.id,
            content: message,
          });
          if (textResult.status === 'failed') {
            toast.warning('A mensagem de texto falhou; enviando apenas o PDF.');
          }
        } catch (textErr: any) {
          logger.error('Erro ao enviar texto da proposta', { error: textErr?.message });
          toast.warning('Não foi possível enviar o texto; enviando apenas o PDF.');
        }
      }

      // PASSO 2: baixa o PDF (bucket privado) via signed URL já existente
      const signedUrl = await getPdfSignedUrl(context.pdfStoragePath ?? null);
      if (!signedUrl) {
        toast.error('O PDF ainda não foi gerado. Gere o PDF antes de enviar.');
        return;
      }

      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error('Falha ao baixar o PDF da proposta.');
      const blob = await response.blob();

      const fileName = `${(context.proposalNumber || 'proposta').replace(/[^\w.-]+/g, '_')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });

      // PASSO 3: documento sem legenda (o texto já foi enviado acima)
      const result = await sendDocumentToConversation({
        conversation: conversationPayload,
        userId: user.id,
        file,
        fileName,
        mimeType: 'application/pdf',
        caption: null,
      });

      if (result.status === 'sent') {
        toast.success('Proposta enviada em PDF pelo chat');
      }

      // v1.5.0: houve envio real ao cliente — agora sim marca como "enviada"
      await markProposalAsSent();

      onOpenConversation(conversationId);
      onOpenChange(false);
    } catch (err: any) {
      logger.error('Erro ao enviar proposta pelo chat', { error: err?.message });
      toast.error(err?.message || 'Não foi possível enviar a proposta pelo chat');
    } finally {
      setPreparing(null);
    }
  };


  /** Salva o texto atual como nova ação rápida (macro) reutilizável */
  const handleSaveMacro = async () => {
    if (!macroName.trim()) {
      toast.error('Informe um nome para a ação rápida.');
      return;
    }
    setSavingMacro(true);
    try {
      const { error } = await supabase.rpc('create_routing_rule', {
        p_payload: {
          name: macroName.trim(),
          description: 'Criada a partir do compartilhamento de proposta',
          condition_type: 'manual_execution',
          action_type: 'send_text',
          action_value: { message: text },
          visibility: 'private',
        } as any,
        p_actions: [] as any,
        p_allowed_user_ids: null as any,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
      toast.success('Ação rápida criada');
      setShowMacroName(false);
      setMacroName('');
      queryClient.invalidateQueries({ queryKey: ['quick-action-rules'] });
    } catch (err: any) {
      logger.error('Erro ao criar ação rápida', { error: err?.message });
      toast.error(err?.message || 'Não foi possível criar a ação rápida');
    } finally {
      setSavingMacro(false);
    }
  };

  const busy = preparing !== null;

  /**
   * Texto exibido no preview: variáveis já resolvidas + link público quando
   * marcado (exatamente o conteúdo que será enviado ao cliente).
   */
  const previewText = useMemo(() => {
    const parts = [resolveVariables(text, context).trim()];
    if (includeLink && context.publicUrl) {
      parts.push(`Proposta online: ${context.publicUrl}`);
    }
    return parts.filter(Boolean).join('\n\n');
  }, [text, context, includeLink]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Camada padrão (z-50). Conteúdo flutuante (Select/Popover/Dropdown) vive em z-[120]. */}
        <DialogContent
          className="sm:max-w-4xl p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col"
          hideCloseButton={false}
        >
          {/* ===== HEADER PREMIUM (mesmo padrão do ScheduleMessageDialog) ===== */}
          <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <DialogHeader className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-base">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </div>
                Compartilhar proposta
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Monte a mensagem e escolha por onde enviar ao cliente.
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* ===== CORPO SPLIT-PANE ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] min-h-0 flex-1">
            {/* ===== PAINEL ESQUERDO — FORMULÁRIO ===== */}
            <ScrollArea className="max-h-[calc(90vh-140px)]">
              <div className="p-5 space-y-4">
                {/* --- SEÇÃO: Proposta & destino --- */}
                <FormSectionCard title="Proposta & destino" icon={FileText}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="text-[10px] h-5">
                      {context.proposalNumber || 'Proposta'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] h-5">
                      {formatCurrency(context.total, context.currency)}
                    </Badge>
                    {context.dealTitle && (
                      <span className="text-xs text-muted-foreground truncate">
                        {context.dealTitle}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Destinatário:{' '}
                    <span className="font-medium text-foreground">
                      {context.contactName || 'Contato não identificado'}
                    </span>
                    {context.contactPhone && (
                      <span className="font-mono ml-1">· {context.contactPhone}</span>
                    )}
                  </p>
                </FormSectionCard>

                {/* --- SEÇÃO: Ação rápida (v1.4.0) --- */}
                <FormSectionCard title="Ação rápida (opcional)" icon={Zap}>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Usar ação rápida como base do texto</Label>
                    <div className="flex items-center gap-1.5">
                      <Select
                        value={selectedMacroId ?? undefined}
                        onValueChange={(id) => {
                          setSelectedMacroId(id);
                          const macro = (macros || []).find((m: any) => m.id === id);
                          const message = macro?.action_value?.message;
                          if (message) setText(String(message));
                        }}
                      >
                        <SelectTrigger className="h-9 flex-1 text-xs">
                          <SelectValue placeholder="Selecione uma ação rápida de texto" />
                        </SelectTrigger>
                        <SelectContent>
                          {(macros || []).map((m: any) => (
                            <SelectItem key={m.id} value={m.id} className="text-xs">
                              <span className="flex items-center gap-2">
                                <Zap className="h-3.5 w-3.5 text-amber-500" />
                                {m.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Visualiza TODAS as ações/sequências da ação rápida selecionada */}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        title="Ver detalhes da ação rápida"
                        disabled={!selectedMacro}
                        onClick={() => setPreviewOpen(true)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>

                      {/* Cria uma nova ação rápida sem sair do compartilhamento */}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        title="Criar nova ação rápida"
                        onClick={() => setCreateMacroOpen(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </FormSectionCard>

                {/* --- SEÇÃO: Mensagem --- */}
                <FormSectionCard title="Mensagem" icon={MessageSquare}>
                  <div className="space-y-2">
                    {/* Barra de ferramentas: formatação WhatsApp + emoji */}
                    <div className="flex items-center gap-0.5 border rounded-md bg-background px-1 py-0.5 w-fit">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Negrito (*texto*)"
                        onClick={() => applyFormatting(textareaRef.current, '*', setText)}
                      >
                        <Bold className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Itálico (_texto_)"
                        onClick={() => applyFormatting(textareaRef.current, '_', setText)}
                      >
                        <Italic className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="Tachado (~texto~)"
                        onClick={() => applyFormatting(textareaRef.current, '~', setText)}
                      >
                        <Strikethrough className="h-3.5 w-3.5" />
                      </Button>
                      <div className="w-px h-5 bg-border mx-1" />
                      <EmojiPicker onSelect={insertAtCursor} className="h-7 w-7" />
                    </div>

                    {/* Textarea principal */}
                    <div className="relative">
                      <Textarea
                        ref={textareaRef}
                        value={text}
                        onChange={(e) => setText(e.target.value.slice(0, 4000))}
                        placeholder="Digite a mensagem... Use a barra acima para formatar, inserir emoji ou clique nas variáveis abaixo."
                        rows={6}
                        maxLength={4000}
                        className="resize-none text-sm"
                      />
                      <span className="absolute bottom-2 right-3 text-[10px] text-muted-foreground pointer-events-none">
                        {text.length}/4000
                      </span>
                    </div>
                  </div>
                </FormSectionCard>

                {/* --- SEÇÃO: Variáveis disponíveis --- */}
                <FormSectionCard title="Variáveis disponíveis" icon={Variable} compact>
                  <p className="text-[10px] text-muted-foreground -mt-1 mb-2">
                    Clique em uma variável para inseri-la na posição do cursor.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {VARIABLES.map((v) => (
                      <Badge
                        key={v.token}
                        variant="secondary"
                        className={cn(
                          'text-[10px] gap-1 transition-all duration-200 cursor-pointer',
                          'hover:bg-primary/15 hover:text-primary hover:shadow-sm hover:scale-[1.03] active:scale-95',
                        )}
                        onClick={() => insertAtCursor(v.token)}
                      >
                        <span className="font-mono">{v.token}</span>
                        <span className="text-muted-foreground font-normal">— {v.label}</span>
                      </Badge>
                    ))}
                  </div>
                </FormSectionCard>

                {/* --- SEÇÃO: Anexos e link --- */}
                <FormSectionCard title="Anexos e link" icon={Paperclip}>
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={includeLink}
                      onCheckedChange={(v) => setIncludeLink(!!v)}
                      disabled={!context.publicUrl}
                    />
                    Incluir link público da proposta
                    {!context.publicUrl && (
                      <span className="text-muted-foreground">(gere o link primeiro)</span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={includePdf}
                      onCheckedChange={(v) => setIncludePdf(!!v)}
                      disabled={!context.pdfStoragePath}
                    />
                    Enviar o arquivo PDF da proposta
                    {!context.pdfStoragePath && (
                      <span className="text-muted-foreground">(gere o PDF primeiro)</span>
                    )}
                  </label>
                  {includePdf && (
                    <InlineNotice variant="info">
                      O arquivo é enviado como documento pelo Chat interno. O WhatsApp Web só
                      aceita texto, então nesse destino o PDF não é anexado.
                    </InlineNotice>
                  )}
                </FormSectionCard>
              </div>
            </ScrollArea>

            {/* ===== PAINEL DIREITO — PREVIEW ===== */}
            <aside className="hidden lg:flex flex-col border-l bg-muted/20 max-h-[calc(90vh-140px)]">
              <div className="flex items-center gap-2 px-4 py-3 border-b bg-background/80 backdrop-blur-sm flex-shrink-0">
                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                <div>
                  <h3 className="text-xs font-semibold">Preview</h3>
                  <p className="text-[10px] text-muted-foreground">Visualização em tempo real</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <ReminderMessagePreview
                  channelType="whatsapp"
                  inboxName={context.contactName ?? undefined}
                  messageText={previewText}
                  messageType="text"
                  /* Simula o anexo PDF no preview quando o envio do arquivo está marcado */
                  attachment={
                    includePdf && context.pdfStoragePath
                      ? {
                          fileName: `${(context.proposalNumber || 'proposta').replace(/[^\w.-]+/g, '_')}.pdf`,
                          caption: 'PDF • Proposta comercial',
                        }
                      : null
                  }
                />

              </div>
            </aside>
          </div>

          {/* ===== FOOTER ===== */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-3 border-t bg-muted/10">
            {/* Salvar o texto atual como nova ação rápida */}
            <div className="flex items-center gap-2">
              {!showMacroName ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs px-2"
                  onClick={() => setShowMacroName(true)}
                >
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  Salvar texto como ação rápida
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    value={macroName}
                    onChange={(e) => setMacroName(e.target.value.slice(0, 80))}
                    placeholder="Nome da ação rápida"
                    className="h-8 text-xs w-48"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={savingMacro}
                    onClick={handleSaveMacro}
                  >
                    {savingMacro ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Salvar'}
                  </Button>
                </div>
              )}
            </div>

            {/* Destinos */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={busy}
                onClick={handleWhatsApp}
              >
                {preparing === 'whatsapp' ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <MessageCircle className="h-3.5 w-3.5 mr-1.5 text-green-600 dark:text-green-400" />
                )}
                Enviar pelo WhatsApp
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-8 text-xs"
                disabled={busy || !context.contactId}
                onClick={() => setInboxPickerOpen(true)}
              >
                {preparing === 'chat' ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                )}
                Enviar pelo Chat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Seleção de inbox para o chat interno (mesma do kanban de negócios) */}
      {context.contactId && (
        <StartChatInboxPicker
          open={inboxPickerOpen}
          onOpenChange={setInboxPickerOpen}
          contactId={context.contactId}
          contactName={context.contactName}
          /* Empilhamento automático via DialogDepthContext (dialog.tsx v2.0.0) */
          onConversationReady={handleConversationReady}
        />
      )}

      {/* v1.4.0: visualização completa da ação rápida (mesmo diálogo do chat) */}
      {selectedMacro && (
        <RulePreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          ruleId={selectedMacro.id}
          ruleName={selectedMacro.name}
          ruleDescription={selectedMacro.description}
        />
      )}

      {/* v1.4.0: criação de nova ação rápida sem sair do compartilhamento */}
      <CreateManualRuleDialog
        open={createMacroOpen}
        onOpenChange={setCreateMacroOpen}
        onSuccess={() => {
          setCreateMacroOpen(false);
          queryClient.invalidateQueries({ queryKey: ['quick-action-rules'] });
        }}
      />
    </>

  );
}
